import { load } from "@cashfreepayments/cashfree-js";
import { toast } from "react-toastify";
import { store } from "../../redux/store";

const PAYMENT_META_KEY = "payment_meta";
let launchedOrderId = null;

export const normalizeGateway = (gateway) =>
  (gateway || "").toString().trim().toLowerCase().replace(/[\s_-]+/g, "");

export const getPaymentPlatform = () => "web";

export const getPaymentMeta = () => {
  try {
    return JSON.parse(localStorage.getItem(PAYMENT_META_KEY) || "{}");
  } catch {
    return {};
  }
};

export const savePaymentMeta = (meta = {}, { replace = false } = {}) => {
  const next = replace ? { ...meta } : { ...getPaymentMeta(), ...meta };
  localStorage.setItem(PAYMENT_META_KEY, JSON.stringify(next));
  return next;
};

export const getPaymentReturnUrl = () =>
  `${window.location.origin}/PaymentVerify?order_id={order_id}&payment_status={payment_status}`;

const getPayGReturnUrl = (orderId) =>
  `${window.location.origin}/PaymentVerify?order_id=${encodeURIComponent(
    orderId || "",
  )}`;

const getPayGWebhookUrl = (orderId) =>
  `${window.location.origin}/webhookUrl?order_id=${encodeURIComponent(
    orderId || "",
  )}`;

const rewritePayGReturnUrl = (paygUrl, orderId) => {
  const returnUrl = getPayGWebhookUrl(orderId);
  const apiBase = (process.env.REACT_APP_API_BASE_URL || "")
    .trim()
    .replace(/\/$/, "");

  const replaceWebhook = (value) => {
    if (!value) return value;
    let next = value;
    if (apiBase) {
      next = next.split(`${apiBase}/webhookUrl`).join(returnUrl);
      next = next
        .split(encodeURIComponent(`${apiBase}/webhookUrl`))
        .join(encodeURIComponent(returnUrl));
    }
    next = next.replace(
      /https?:\/\/[^/]+\/webhookUrl/gi,
      returnUrl.replace(/[?].*$/, ""),
    );
    return next;
  };

  try {
    const parsed = new URL(paygUrl);
    for (const [key, value] of [...parsed.searchParams.entries()]) {
      parsed.searchParams.set(key, replaceWebhook(value));
    }
    return parsed.toString();
  } catch {
    return replaceWebhook(paygUrl);
  }
};

const isSandboxMode = (appMode) => {
  const mode = (appMode || "").toString().toLowerCase();
  return mode === "test" || mode === "sandbox";
};

export const getMetalNameFromMeta = (paymentMeta = getPaymentMeta()) => {
  if (typeof paymentMeta?.metal === "string") return paymentMeta.metal;
  return paymentMeta?.metal?.metalName || paymentMeta?.metalName || "";
};

export const buildVerifyPayload = (orderId, paymentMeta = getPaymentMeta()) => {
  const metalName = getMetalNameFromMeta(paymentMeta);
  return {
    signature: "",
    orderId,
    orderType: paymentMeta.orderType,
    data: "",
    weight: paymentMeta.grams,
    metalName: metalName || paymentMeta.metal,
    amount: paymentMeta.totalAmount,
    buyType: paymentMeta.buyType,
    transactionMode: paymentMeta.selectedGateway,
    planid: paymentMeta.planid || "",
    moneysip: Boolean(
      paymentMeta.moneysip ||
        metalName === "Money" ||
        paymentMeta.metal === "Money",
    ),
    x_api_version: "2022-09-01",
    jeweller: process.env.REACT_APP_JEWELLER_ID,
  };
};

const persistOrderId = (orderId) => {
  if (orderId) savePaymentMeta({ orderId: String(orderId) });
};

const redirectTo = (url) => {
  window.location.assign(url);
};

export const handleCashfreePayment = async (data, options = {}) => {
  const state = store.getState();
  const appMode = state?.dashboard?.appConfig?.appMode;
  const cashfreeMode = isSandboxMode(appMode) ? "sandbox" : "production";
  const paymentSessionId = data?.token;
  const orderId = data?.orderId;

  if (!paymentSessionId) {
    throw new Error("Cashfree payment session is missing");
  }

  persistOrderId(orderId);

  const cashfree = await load({ mode: cashfreeMode });
  const returnUrl = getPaymentReturnUrl();
  const useSubscription =
    Boolean(options.autodebit) &&
    typeof cashfree.subscriptionsCheckout === "function";

  const result = useSubscription
    ? await cashfree.subscriptionsCheckout({
        subsSessionId: paymentSessionId,
        redirectTarget: "_self",
        returnUrl,
      })
    : await cashfree.checkout({
        paymentSessionId,
        redirectTarget: "_self",
        returnUrl,
      });

  if (result && typeof result.pay === "function") {
    result.pay();
  }

  if (result?.error) {
    throw new Error(result.error.message || "Cashfree checkout failed");
  }
};

const PAYG_POP_DELAY_MS = 2000;

const readPopupHref = (popup) => {
  try {
    return popup?.location?.href || "";
  } catch {
    return "";
  }
};

const isGatewayReturn = (href) =>
  href.includes("/webhookUrl") || href.includes("/PaymentVerify");

export const handlePayGPayment = async (data) => {
  const rawUrl = data?.token;
  const orderId = data?.orderId;

  if (!rawUrl || !orderId) {
    throw new Error("Unable to process your PayG order");
  }

  const url = rewritePayGReturnUrl(rawUrl, orderId);
  persistOrderId(orderId);

  return new Promise((resolve) => {
    const popup = window.open(url, "payg_checkout", "width=480,height=720");

    if (!popup) {
      window.location.assign(url);
      resolve();
      return;
    }

    let settled = false;
    let webhookHandled = false;
    let leftApp = false;
    let pollTimer = null;
    let popTimer = null;

    const cleanup = () => {
      clearInterval(pollTimer);
      clearTimeout(popTimer);
      document.removeEventListener("visibilitychange", onResume);
    };

    const finish = () => {
      if (settled) return;
      settled = true;
      cleanup();
      try {
        popup.close();
      } catch {}
      window.location.assign(getPayGReturnUrl(orderId));
      resolve();
    };

    // WebView onNavigationRequest: block /webhookUrl, then pop after 2 seconds.
    const onWebhookUrl = () => {
      if (webhookHandled || settled) return;
      webhookHandled = true;
      clearInterval(pollTimer);
      try {
        popup.close();
      } catch {}
      popTimer = setTimeout(finish, PAYG_POP_DELAY_MS);
    };

    // FireUpiIntent: returning to the app closes the payment screen.
    const onResume = () => {
      if (document.visibilityState === "hidden") {
        leftApp = true;
        return;
      }
      if (!leftApp || settled || webhookHandled) return;

      if (!popup || popup.closed) {
        finish();
        return;
      }

      if (isGatewayReturn(readPopupHref(popup))) {
        onWebhookUrl();
      }
    };

    document.addEventListener("visibilitychange", onResume);

    pollTimer = setInterval(() => {
      if (settled || webhookHandled) return;

      const href = readPopupHref(popup);
      if (isGatewayReturn(href)) {
        onWebhookUrl();
        return;
      }

      if (!popup || popup.closed) {
        finish();
      }
    }, 300);
  });
};

export const handlePaytmPayment = async (data) => {
  const token = data?.token;
  const orderId = data?.orderId;

  if (!token || !orderId) {
    throw new Error("Unable to process your Paytm order");
  }

  persistOrderId(orderId);

  if (typeof token === "string" && /^https?:\/\//i.test(token)) {
    redirectTo(token);
    return;
  }

  const state = store.getState();
  const appMode = state?.dashboard?.appConfig?.appMode;
  const base = isSandboxMode(appMode)
    ? "https://securegw-stage.paytm.in"
    : "https://securegw.paytm.in";

  redirectTo(
    `${base}/theia/api/v1/showPaymentPage?orderId=${encodeURIComponent(
      orderId,
    )}&token=${encodeURIComponent(token)}`,
  );
};

export const handleCcAvenuePayment = async (data) => {
  const url = data?.url;
  const encRequest = data?.encVal;
  const accessCode = data?.accessCode;
  const command = data?.command || "";
  const orderId = data?.orderId;

  if (!url || !encRequest || !accessCode) {
    throw new Error("Unable to process your CCAvenue order");
  }

  persistOrderId(orderId);

  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  form.style.display = "none";

  const addHidden = (name, value) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value ?? "";
    form.appendChild(input);
  };

  addHidden("command", command);
  addHidden("encRequest", encRequest);
  addHidden("access_code", accessCode);

  document.body.appendChild(form);
  form.submit();
};

export const handleNomodPayment = async (data) => {
  const paymentUrl = data?.checkout_url || data?.token;
  const orderId = data?.orderId;

  if (!paymentUrl || !orderId) {
    throw new Error("Unable to process your Nomod order");
  }

  persistOrderId(orderId);
  redirectTo(paymentUrl);
};

export const launchPaymentGateway = async (
  gateway,
  orderData,
  options = {},
) => {
  const name = normalizeGateway(gateway);
  const orderId = orderData?.orderId ? String(orderData.orderId) : "";

  if (orderId && launchedOrderId === orderId) {
    return;
  }
  if (orderId) {
    launchedOrderId = orderId;
  }

  savePaymentMeta({
    orderId,
    selectedGateway: name,
    autodebit: Boolean(options.autodebit),
  });

  switch (name) {
    case "cashfree":
      await handleCashfreePayment(orderData, options);
      break;
    case "payg":
      await handlePayGPayment(orderData);
      break;
    case "paytm":
      await handlePaytmPayment(orderData);
      break;
    case "ccavenue":
      await handleCcAvenuePayment(orderData);
      break;
    case "nomod":
      await handleNomodPayment(orderData);
      break;
    default:
      launchedOrderId = null;
      throw new Error("Selected payment gateway is not supported");
  }
};

export const startPaymentFromOrderResponse = async (
  orderResponse,
  gateway,
  options = {},
) => {
  if (!normalizeGateway(gateway)) return false;

  if (!orderResponse?.success) {
    toast.error(orderResponse?.message || "Order creation failed");
    return false;
  }

  const orderData = orderResponse.data;
  if (!orderData) {
    toast.error(orderResponse?.message || "Unable to process your order");
    return false;
  }

  try {
    await launchPaymentGateway(gateway, orderData, options);
    return true;
  } catch (err) {
    launchedOrderId = null;
    console.error("Payment gateway error:", err);
    toast.error(err?.message || "Payment failed");
    return false;
  }
};
